import React, { useState, useEffect } from "react";
import { X, Palette, Lock, ArrowLeft } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { useAuth } from "@/context/AuthContext";

const GEMS = [
  { key: "sapphire", tint: "#6B9EE8", border: "rgba(15, 82, 186, 0.35)", glow: "rgba(15, 82, 186, 0.18)" },
  { key: "ruby",     tint: "#E86B8C", border: "rgba(224, 17, 95, 0.35)", glow: "rgba(224, 17, 95, 0.16)" },
  { key: "emerald",  tint: "#6BE895", border: "rgba(80, 200, 120, 0.35)", glow: "rgba(80, 200, 120, 0.16)" },
];

// If EN images exist later, we'll switch by lang.
const imgFor = (gem, lang) => {
  const base = `/fiches/color/${gem}`;
  // EN not yet delivered; fall back to FR file for now.
  const path = lang === "en" ? `${base}-en.png` : `${base}-fr.png`;
  return { primary: path, fallback: `${base}-fr.png` };
};

export const ColorClassificationModal = ({ open, onClose, onSubscribe }) => {
  const { lang, t } = useLang();
  const { subscribed } = useSubscription();
  const { user } = useAuth();
  const isSubscribed = true; // free access — no subscription required
  const [tab, setTab] = useState("sapphire");
  const [zoomSrc, setZoomSrc] = useState(null);

  useEffect(() => { if (!open) { setZoomSrc(null); setTab("sapphire"); } }, [open]);
  if (!open) return null;

  const active = GEMS.find((g) => g.key === tab) || GEMS[0];
  const { primary, fallback } = imgFor(active.key, lang);

  const heading = lang === "fr" ? "Classification des couleurs" : "Color Classification";
  const subhead = lang === "fr"
    ? "Nuances et intensités reconnues pour saphirs, rubis et émeraudes"
    : "Recognised hues and intensities for sapphires, rubies and emeralds";

  return (
    <div
      data-testid="color-classification-modal"
      className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8 bg-black/85 backdrop-blur-md overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-5xl my-6 rounded-sm bg-[#050B1E] border p-4 md:p-10"
        style={{ borderColor: active.border, boxShadow: `inset 0 0 120px ${active.glow}` }}
      >
        <button
          data-testid="color-close-btn"
          onClick={onClose}
          className="absolute top-4 right-4 text-[#A1A7B3] hover:text-[#F8F5EE] transition-colors z-10"
          aria-label={t.modal.close}
        >
          <X size={22} strokeWidth={1.4} />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <Palette size={22} strokeWidth={1.4} className="text-[#C1A661]" />
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C1A661]">
              {lang === "fr" ? "Fiche couleur" : "Colour sheet"}
            </div>
            <h2 className="font-serif text-2xl md:text-4xl font-light text-[#F8F5EE] leading-tight">
              {heading}
            </h2>
            <p className="mt-1 text-[12px] text-[#A1A7B3] font-light italic">{subhead}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-[#C1A661]/15 pb-3" data-testid="color-tabs">
          {GEMS.map((g) => {
            const activeTab = g.key === tab;
            return (
              <button
                key={g.key}
                data-testid={`color-tab-${g.key}`}
                onClick={() => setTab(g.key)}
                className={`px-4 py-2 rounded-full border text-[11px] tracking-[0.25em] uppercase transition-colors ${
                  activeTab
                    ? "border-[#C1A661] bg-[#C1A661]/15 text-[#F8F5EE]"
                    : "border-[#C1A661]/30 text-[#A1A7B3] hover:text-[#E2CFA4] hover:border-[#C1A661]/60"
                }`}
                style={activeTab ? { color: "#F8F5EE" } : {}}
              >
                {t.gems[g.key]}
              </button>
            );
          })}
        </div>

        {isSubscribed ? (
          <div className="space-y-4" data-testid="color-content">
            <button
              type="button"
              onClick={() => setZoomSrc(primary)}
              className="block w-full text-left"
              aria-label={t.fiche.viewFullSize}
            >
              <img
                key={primary}
                src={primary}
                alt={heading}
                data-testid="color-image"
                className="w-full h-auto rounded-sm border cursor-zoom-in hover:brightness-105 transition"
                style={{ borderColor: active.border, backgroundColor: "#F8F5EE" }}
                loading="lazy"
                onError={(e) => { if (e.currentTarget.src.indexOf(fallback) < 0) e.currentTarget.src = fallback; }}
              />
            </button>
            <button
              type="button"
              onClick={() => setZoomSrc(primary)}
              className="inline-block text-[10px] tracking-[0.25em] uppercase text-[#C1A661] hover:text-[#E2CFA4] transition-colors"
            >
              {t.fiche.viewFullSize} ↗
            </button>
          </div>
        ) : (
          <div className="relative border border-[#C1A661]/20 rounded-sm overflow-hidden">
            <img
              src={fallback}
              alt={heading}
              className="w-full h-auto filter blur-md select-none pointer-events-none"
              style={{ backgroundColor: "#F8F5EE" }}
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#050B1E]/70 backdrop-blur-sm p-6 text-center">
              <Lock size={28} strokeWidth={1.4} className="text-[#C1A661] mb-3" />
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#C1A661] font-medium mb-2">
                {t.fiche.lockedTitle}
              </div>
              <p className="text-sm text-[#D8DBE2] font-light max-w-md mb-5">
                {t.fiche.lockedBody}
              </p>
              <button
                data-testid="color-subscribe-btn"
                onClick={onSubscribe}
                className="px-6 py-3 rounded-full bg-[#C1A661] hover:bg-[#E2CFA4] text-[#050B1E] text-xs tracking-[0.25em] uppercase font-semibold transition-colors"
              >
                {t.fiche.lockedCta}
              </button>
            </div>
          </div>
        )}
      </div>

      {zoomSrc && (
        <div
          data-testid="color-zoom-overlay"
          className="fixed inset-0 z-[60] bg-black/95 flex flex-col"
          onClick={(e) => { e.stopPropagation(); setZoomSrc(null); }}
        >
          <div className="flex items-center justify-between p-4 border-b border-[#C1A661]/20 bg-[#050B1E]/95">
            <button
              data-testid="color-zoom-back-btn"
              onClick={(e) => { e.stopPropagation(); setZoomSrc(null); }}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#C1A661]/40 hover:border-[#C1A661] text-[#E2CFA4] hover:text-[#F8F5EE] text-xs tracking-[0.25em] uppercase transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={1.5} />
              <span>{lang === "fr" ? "Retour" : "Back"}</span>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setZoomSrc(null); }}
              className="text-[#A1A7B3] hover:text-[#F8F5EE] transition-colors"
              aria-label={t.modal.close}
            >
              <X size={22} strokeWidth={1.4} />
            </button>
          </div>
          <div className="flex-1 overflow-auto p-4 md:p-8 flex items-start justify-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={zoomSrc}
              alt={heading}
              className="max-w-full h-auto rounded-sm shadow-2xl"
              style={{ backgroundColor: "#F8F5EE" }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
